import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { PopupboxManager, PopupboxContainer } from 'react-popupbox';
import ModalVideo from 'react-modal-video';

import 'react-popupbox/dist/react-popupbox.css';
import 'react-modal-video/css/modal-video.min.css';

// HOC COMPONENTS
import withMediaQuery from '@/hoc/withMediaQuery';

// UTILITY
import { customAnalyticsEvent } from '@/utility/analytics';

// COMPONENTS
import { SwiperCarousel, SwiperSlide } from '@/components/Carousels/SwiperCarousel';

// STYLES
import { Container, Content, Thumbnail } from './styles';

function getItemsCount(length) {
  switch (length) {
    case 1:
      return 1;
    case 2:
      return 2;
    default:
      return 3;
  }
}

class ContentGallery extends PureComponent {
  state = {
    modalVideo: false,
    videoData: {},
    galleryItems: [],
  };

  componentDidMount() {
    this.formatGalleryItems();
  }

  formatGalleryItems = () => {
    const { data } = this.props;
    const galleryItems = [];

    if (data.trailers) {
      data.trailers.forEach(item => {
        galleryItems.push({ ...item, url: `${item.url}?autoplay=1`, type: 'video' });
      });
    }

    if (data.images) {
      data.images.forEach(url => {
        galleryItems.push({ url, type: 'photo' });
      });
    }

    this.setState({ galleryItems });
  };

  manageModalVideoState = state => {
    const videoData = !state ? { videoData: {} } : {};

    this.setState({ modalVideo: state, ...videoData });
  };

  handleItemClick = data => {
    const {
      content: { id, content_title },
    } = this.props;

    if (data.type === 'photo') {
      const content = <img src={data.url} alt="detail" />;

      PopupboxManager.open({
        content,
        config: {
          titleBar: {
            enable: true,
          },
          fadeIn: true,
          fadeInSpeed: 500,
        },
      });
    } else if (data.type === 'video') {
      this.setState({ videoData: data }, () => this.manageModalVideoState(true));

      customAnalyticsEvent('Played Video', {
        content_title,
        content_id: id,
        video_url: data.url,
      });
    }
  };

  getThumbnail = videoData => {
    const { data } = this.props;
    let img = (!!data.images && data.images[0]) || '';

    if (videoData.source === 'youtube') {
      img = `https://img.youtube.com/vi/${videoData.key}/hqdefault.jpg`;
    }

    return img ? <img src={img} alt="thumb" /> : null;
  };

  render() {
    const { galleryItems, modalVideo, videoData } = this.state;
    const { downXs } = this.props;

    const carouselOptions = {
      navigation: false,
      pagination: {
        clickable: true,
        dynamicBullets: true,
      },
      watchOverflow: true,
      allowTouchMove: galleryItems.length > 2 || downXs,
      breakpoints: {
        0: {
          slidesPerView: 1.06,
          centeredSlides: true,
        },
        600: {
          slidesPerView: getItemsCount(galleryItems.length),
          centeredSlides: false,
        },
      },
    };

    if (!galleryItems.length) return null;

    return (
      <Container className="page-content page-content--no-padding gallery-wrapper">
        <Content>
          {!!galleryItems.length && (
            <SwiperCarousel options={carouselOptions}>
              {galleryItems.map((item, index) => {
                return (
                  <SwiperSlide key={index}>
                    <Thumbnail onClick={() => this.handleItemClick(item)}>
                      {item.type === 'video' ? (
                        <>
                          {this.getThumbnail(item)}
                          <div className="play-fake">
                            <i className="icon-play" />
                          </div>
                        </>
                      ) : (
                        <img src={item.url} alt="thumb" />
                      )}
                    </Thumbnail>
                  </SwiperSlide>
                );
              })}
            </SwiperCarousel>
          )}
        </Content>
        <PopupboxContainer />
        {!!videoData.url && (
          <ModalVideo
            videoId={videoData.key}
            channel={videoData.source}
            isOpen={modalVideo}
            youtube={{
              autoplay: 1,
              modestbranding: 1,
            }}
            onClose={() => this.manageModalVideoState(false)}
          />
        )}
      </Container>
    );
  }
}

ContentGallery.propTypes = {
  data: PropTypes.object.isRequired,
  content: PropTypes.object.isRequired,
  downXs: PropTypes.bool.isRequired,
};

export default compose(withMediaQuery({ downXs: theme => theme.breakpoints.down('xs') }))(
  ContentGallery,
);
